import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tab, Tabs, Typography } from '@mui/material';
import SourceIcon from '@mui/icons-material/Source';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import CloseIcon from '@mui/icons-material/Close';
import { useUserContext } from '@/contexts/userContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import TabPanel from '@/components/tabPanel';
import PipelineBuilder from '@/components/pipelineBuilder/pipelineBuilder';
import { useApiContext } from '@/contexts/apiContext';
import EntityConfiguration from '../entities/configuration';
import ProgressIndicator from '../progressIndicator';
import ProcessedItems from './itemProcessingStatuses';
import AgentInstanceAdministration from './administration';
import ErrorMessage from '../errorMessage';
import ViewDataSourceItem from './viewDataSourceItem';
import AgentHeader from './header';
import { useNotificationContext } from '@/contexts/notificationContext';

type Props = {};

const AgentInstance = ({}: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useNotificationContext();

  const { agentDefId, agentInstanceId } = useParams();

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const containerHeight = containerSize.height;
  const topPanelHeight = topPanelSize.height;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [agentInstance, setAgentInstance] = useState<any>();
  const [selectedTab, setSelectedTab] = useState<string>('itemProcessingStatuses');
  const [openItems, setOpenItems] = useState<any[]>([]);
  const [optionTabs, setOptionTabs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isExecutingAgent, setIsExecutingAgent] = useState(false);
  const [error, setError] = useState<any>();
  const [suppressEvents, setSuppressEvents] = useState<boolean>(false);

  const menuOpen = Boolean(menuAnchorEl);

  const tabs = useMemo(() => {
    const baseTabs: any[] = [];
    if (hasPermission(`pipeline:agents:items:view`, agentInstance?.userAccess)) {
      baseTabs.push({
        value: 'itemProcessingStatuses',
        label: 'Processed Items',
        icon: <SourceIcon sx={{ fontSize: 18 }} />,
      });
    }
    if (hasPermission(`pipeline:agents:configuration:view`, agentInstance?.userAccess)) {
      baseTabs.push({
        value: 'configuration',
        label: 'Configuration',
        icon: <SettingsOutlinedIcon sx={{ fontSize: 18 }} />,
      });
    }

    const allTabs = [...baseTabs];

    optionTabs.forEach((ot) => {
      allTabs.push({ ...ot, isOption: true });
    });

    openItems.forEach((item) => {
      allTabs.push({
        value: item.id,
        label: item.itemName ? item.itemName : `Group : ${item.dataSourceItemGroup}`,
        icon: <SourceIcon sx={{ fontSize: 18 }} />,
        isItem: true,
      });
    });

    return allTabs;
  }, [agentInstance, optionTabs, openItems, hasPermission, t]);

  useEffect(() => {
    const getAgentInstance = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getAgentInstance(agentDefId as string, agentInstanceId as string);
        setAgentInstance(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (agentDefId && agentInstanceId) {
      getAgentInstance();
    }
  }, [agentDefId, agentInstanceId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const onOpenItem = (item: any) => {
    const i = openItems.find((i) => i.id === item.id);
    if (i) {
      setSelectedTab(i.id);
    } else {
      setOpenItems([...openItems, item]);
      setSelectedTab(item.id);
    }
  };

  const addOptionsTab = (option: any) => {
    const o = optionTabs.find((o) => o.value === option.value);
    if (o) {
      setSelectedTab(o.value);
    } else {
      setOptionTabs([...optionTabs, option]);
      setSelectedTab(option.value);
    }
    setMenuAnchorEl(null);
  };

  const deleteItemTab = (itemId: string) => {
    setOpenItems(openItems.filter((i) => i.id !== itemId));
    if (selectedTab === itemId) {
      setSelectedTab('itemProcessingStatuses');
    }
  };

  const deleteOptionTab = (value: string) => {
    setOptionTabs(optionTabs.filter((o) => o.value !== value));
    if (selectedTab === value) {
      setSelectedTab('itemProcessingStatuses');
    }
  };

  const openOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const updateAgentInstance = async (updatedAgent: any) => {
    const { id, tenantId, name, description, entityType, entityDefId, settings, eventSubscriptions } = updatedAgent;

    try {
      await apiContext.updateAgentInstance(entityDefId as string, id, {
        id,
        tenantId,
        name,
        description,
        entityType,
        entityDefId,
        settings,
        eventSubscriptions,
      });
      setAgentInstance(updatedAgent);
      toast({ message: 'Changes saved' });
    } catch (error) {
      setError(error);
    }
  };

  const reloadAgentInstance = async () => {
    try {
      const response = await apiContext.getAgentInstance(agentDefId as string, agentInstanceId as string);
      setAgentInstance(response);
    } catch (error) {
      setError(error);
    }
  };

  const handleExecuteAgent = async () => {
    if (!agentDefId || !agentInstanceId) return;
    setIsExecutingAgent(true);
    setError(null);
    try {
      const res = await apiContext.executeAgentInstance(agentDefId, agentInstanceId, {
        suppressEvents,
      });
      const isFailed = res?.status === 'failed' || Boolean(res?.error) || (res?.failedCount > 0 && res?.successCount === 0);
      const isPartial = res?.status === 'partially_completed' || (res?.failedCount > 0 && res?.successCount > 0);
      const errMsg = res?.error || res?.items?.find((i: any) => i.error)?.error;

      if (isFailed) {
        const finalMsg = errMsg || 'Agent execution failed.';
        setError(finalMsg);
        toast({
          message: finalMsg,
          type: 'error',
        });
      } else if (isPartial) {
        const warnMsg = `Agent completed with issues: ${res?.successCount ?? 0} succeeded, ${res?.failedCount ?? 0} failed.${errMsg ? ` Error: ${errMsg}` : ''}`;
        setError(warnMsg);
        toast({
          message: warnMsg,
          type: 'warning',
        });
      } else {
        const count = res?.totalItems ?? res?.items?.length ?? 0;
        toast({
          message: `Agent executed successfully${count ? ` (${count} items processed)` : ''}${suppressEvents ? ' (this step only)' : ''}`,
          type: 'success',
        });
      }
      reloadAgentInstance();
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.items?.find((i: any) => i.error)?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Agent execution failed.';
      setError(errMsg);
      toast({
        message: errMsg,
        type: 'error',
      });
      reloadAgentInstance();
    } finally {
      setIsExecutingAgent(false);
    }
  };

  const close = () => {
    const from = location.state?.from || '/instances?tab=agentInstances';
    navigate(from);
  };

  if (loading && !agentInstance) {
    return <ProgressIndicator />;
  }

  return (
    <Stack
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
      ref={containerRef}
    >
      {/* Top Panel: Header + Tabs */}
      <Box ref={topPanelRef} sx={{ display: 'flex', flexDirection: 'column', zIndex: 10 }}>
        <AgentHeader
          agentInstance={agentInstance}
          onClose={close}
          onExecute={handleExecuteAgent}
          isExecuting={isExecutingAgent}
          suppressEvents={suppressEvents}
          onToggleSuppressEvents={setSuppressEvents}
        />
        {error && <ErrorMessage error={error} onClose={() => setError(null)} />}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          sx={{ pb: { xs: 1, sm: 0 } }}
        >
          <Box sx={{ maxWidth: { xs: '100%', sm: '75vw' }, width: '100%', bgcolor: 'background.paper' }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="secondary"
              variant="scrollable"
              sx={{
                minHeight: '40px',
                '& .MuiTabs-indicator': {
                  height: '3px',
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  icon={tab.icon}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    textTransform: 'none',
                    fontWeight: 600,
                    position: 'relative',
                    overflow: 'visible',
                  }}
                  label={
                    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', pr: (tab.isOption || tab.isItem) ? 1.5 : 0 }}>
                      <Typography variant="inherit" sx={{ maxWidth: '150px' }} noWrap>
                        {t(tab.label)}
                      </Typography>
                      {(tab.isOption || tab.isItem) && (
                        <IconButton
                          size="small"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (tab.isOption) deleteOptionTab(tab.value);
                            else deleteItemTab(tab.value);
                          }}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -4,
                            p: 0.1,
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 10 }} />
                        </IconButton>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
          <Stack
            direction={'row'}
            alignItems="center"
            spacing={1.5}
            sx={{ px: 2, py: { xs: 1, sm: 0 }, pb: { xs: 1.5, sm: 0 } }}
          >
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={openOptionsMenu}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                borderRadius: '20px',
                px: 2,
                bgcolor: 'primary.main',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(35, 53, 101, 0.2)',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 6px 16px rgba(35, 53, 101, 0.3)' },
                textTransform: 'none',
              }}
            >
              {t('common.buttons.options')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative', mt: 1 }}>
        {containerHeight > 0 && topPanelHeight > 0 && (
          <>
            {hasPermission(`pipeline:agents:items:view`, agentInstance?.userAccess) && (
              <TabPanel
                value="itemProcessingStatuses"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height}
              >
                <ProcessedItems
                  agentInstance={agentInstance}
                  onOpenItem={onOpenItem}
                  maxHeight={containerSize.height - topPanelSize.height}
                />
              </TabPanel>
            )}

            {hasPermission(`pipeline:agents:configuration:view`, agentInstance?.userAccess) && (
              <TabPanel
                value="configuration"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height}
              >
                <EntityConfiguration
                  maxHeight={containerSize.height - topPanelSize.height}
                  entity={agentInstance}
                  onSubmit={updateAgentInstance}
                />
              </TabPanel>
            )}

            {hasPermission(`pipeline:agents:configuration:view`, agentInstance?.userAccess) && (
              <TabPanel
                value="pipelineBuilder"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height}
              >
                <PipelineBuilder
                  maxHeight={containerSize.height - topPanelSize.height}
                  entity={agentInstance}
                  isOpen={selectedTab === 'pipelineBuilder'}
                  onConfigurationChange={reloadAgentInstance}
                />
              </TabPanel>
            )}

            {(hasPermission(`pipeline:agents:accessControl:view`, agentInstance?.userAccess) ||
              hasPermission(`pipeline:agents:usageStats:view`, agentInstance?.userAccess) ||
              hasPermission(`pipeline:agents:auditTrail:view`, agentInstance?.userAccess)) && (
              <TabPanel
                value="administration"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height}
              >
                <AgentInstanceAdministration
                  isOpen={selectedTab === 'administration'}
                  maxHeight={containerSize.height - topPanelSize.height}
                  agentInstance={agentInstance}
                />
              </TabPanel>
            )}

            {openItems.map((item) => (
              <TabPanel
                key={item.id}
                value={item.id}
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height}
              >
                <ViewDataSourceItem
                  agentInstance={agentInstance}
                  item={item}
                  maxHeight={containerSize.height - topPanelSize.height}
                />
              </TabPanel>
            ))}
          </>
        )}
      </Box>

      <Menu
        id="optionsMenu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            minWidth: 180,
          },
        }}
      >
        {hasPermission(`pipeline:agents:configuration:view`, agentInstance?.userAccess) && (
          <MenuItem
            onClick={() =>
              addOptionsTab({
                value: 'pipelineBuilder',
                label: 'Pipeline',
                icon: <PlumbingIcon fontSize="small" />,
              })
            }
          >
            <ListItemIcon>
              <PlumbingIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>{t('Pipeline')}</ListItemText>
          </MenuItem>
        )}
        {(hasPermission(`pipeline:agents:accessControl:view`, agentInstance?.userAccess) ||
          hasPermission(`pipeline:agents:usageStats:view`, agentInstance?.userAccess) ||
          hasPermission(`pipeline:agents:auditTrail:view`, agentInstance?.userAccess)) && (
          <MenuItem
            onClick={() =>
              addOptionsTab({
                value: 'administration',
                label: 'App administration',
                icon: <AdminPanelSettingsIcon fontSize="small" />,
              })
            }
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>
              {t('Administration')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
};

export default AgentInstance;
